import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  format,
  isToday,
} from "date-fns";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { ChevronLeft, ChevronRight, StickyNote } from "lucide-react";
import { cn } from "../lib/utils";

interface Employee {
  _id: string;
  fullName: string;
  email: string;
  designation: string;
  department: string;
  company: string;
  employeeType: string;
}

interface Note {
  _id: string;
  date: string;
  content: string;
  employee: Employee;
  createdAt: string;
  updatedAt: string;
}

export default function AdminNotes() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchNotes();
  }, [currentDate]);

  const fetchNotes = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("authToken");
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/notes/admin/all?year=${year}&month=${month}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setNotes(response.data.notes || []);
    } catch (error) {
      console.error("Error fetching notes:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek };
  };

  const hasNoteOnDate = (day: number) => {
    const targetDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    const dateStr = format(targetDate, "yyyy-MM-dd");
    return notes.some((note) => note.date === dateStr);
  };

  const getNoteCountOnDate = (day: number) => {
    const targetDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    const dateStr = format(targetDate, "yyyy-MM-dd");
    return notes.filter((note) => note.date === dateStr).length;
  };

  const handleDateClick = (day: number) => {
    const selectedDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    navigate(`/notes/date/${dateStr}`);
  };

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      if (direction === "prev") {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
  const monthYear = format(currentDate, "MMMM yyyy");

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const today = new Date();
  const isCurrentMonth =
    today.getMonth() === currentDate.getMonth() &&
    today.getFullYear() === currentDate.getFullYear();

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Employee Notes & Tasks</h1>
          <p className="text-muted-foreground mt-1">
            Click on any date to view notes from all employees
          </p>
        </div>
        <Button onClick={goToToday} variant="outline">
          Today
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl">{monthYear}</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigateMonth("prev")}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigateMonth("next")}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center h-96">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1 max-w-2xl mx-auto">
              {/* Week day headers */}
              {weekDays.map((day) => (
                <div
                  key={day}
                  className="text-center font-semibold text-xs py-1 text-muted-foreground"
                >
                  {day}
                </div>
              ))}

              {/* Empty cells for days before month starts */}
              {Array.from({ length: startingDayOfWeek }).map((_, index) => (
                <div key={`empty-${index}`} className="aspect-square" />
              ))}

              {/* Calendar days */}
              {Array.from({ length: daysInMonth }).map((_, index) => {
                const day = index + 1;
                const dateObj = new Date(
                  currentDate.getFullYear(),
                  currentDate.getMonth(),
                  day
                );
                const isDateToday = isToday(dateObj);
                const hasNote = hasNoteOnDate(day);
                const noteCount = getNoteCountOnDate(day);

                return (
                  <button
                    key={day}
                    onClick={() => handleDateClick(day)}
                    className={cn(
                      "aspect-square p-1 rounded-md border transition-all relative",
                      "hover:border-primary hover:bg-primary/5",
                      isDateToday &&
                        "border-primary bg-primary/10 font-semibold",
                      !isDateToday && !hasNote && "border-border",
                      hasNote &&
                        "border-green-500 bg-green-50 dark:bg-green-950"
                    )}
                  >
                    <div className="flex flex-col items-center justify-center h-full">
                      <span className="text-xs">{day}</span>
                      {hasNote && (
                        <div className="flex items-center gap-0.5 mt-0.5">
                          <StickyNote className="h-2.5 w-2.5 text-green-600" />
                          {noteCount > 1 && (
                            <span className="text-[8px] font-semibold text-green-700 dark:text-green-400">
                              {noteCount}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Legend */}
          <div className="flex items-center gap-6 mt-6 pt-6 border-t">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-primary bg-primary/10" />
              <span className="text-sm text-muted-foreground">Today</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-green-500 bg-green-50" />
              <span className="text-sm text-muted-foreground">Has Notes</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

